import type { ReactNode } from 'react'
import type { CartItem } from '@/types'
import type { Rates } from '@/lib/shop/money'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

import { BASE_CURRENCY, CURRENCIES, MAX_CART_LINES } from '@/constants/shop'

const CART_KEY = 'drawsword-cart'
const CURRENCY_KEY = 'drawsword-currency'

type ShopContextValue = {
    items: Array<CartItem>
    count: number
    addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void
    setQuantity: (id: string, quantity: number) => void
    removeItem: (id: string) => void
    clearCart: () => void
    cartOpen: boolean
    setCartOpen: (open: boolean) => void
    currency: string
    setCurrency: (currency: string) => void
    rates: Rates | null
}

const ShopContext = createContext<ShopContextValue | null>(null)

interface ShopProviderProps {
    children: ReactNode
}

export const ShopProvider = ({ children }: ShopProviderProps): ReactNode => {
    const [items, setItems] = useState<Array<CartItem>>([])
    const [loaded, setLoaded] = useState(false)
    const [cartOpen, setCartOpen] = useState(false)
    const [currency, setCurrencyState] = useState(BASE_CURRENCY)
    const [rates, setRates] = useState<Rates | null>(null)

    useEffect(() => {
        try {
            const savedCart = localStorage.getItem(CART_KEY)

            if (savedCart) {
                const parsed = JSON.parse(savedCart)

                if (Array.isArray(parsed)) setItems(parsed.slice(0, MAX_CART_LINES))
            }

            const savedCurrency = localStorage.getItem(CURRENCY_KEY)

            if (savedCurrency && CURRENCIES.includes(savedCurrency)) setCurrencyState(savedCurrency)
        }
        catch {
            // A corrupt saved cart is simply ignored.
        }

        setLoaded(true)

        fetch('/api/rates')
            .then(response => response.json())
            .then((data: { rates: Rates | null }) => setRates(data.rates ?? null))
            .catch(() => setRates(null))
    }, [])

    useEffect(() => {
        if (!loaded) return

        try {
            localStorage.setItem(CART_KEY, JSON.stringify(items))
        }
        catch {
            // Storage can be full or blocked; the cart still works for this visit.
        }
    }, [items, loaded])

    const setCurrency = useCallback((next: string) => {
        if (!CURRENCIES.includes(next)) return

        setCurrencyState(next)

        try {
            localStorage.setItem(CURRENCY_KEY, next)
        }
        catch {
            // Ignore blocked storage.
        }
    }, [])

    const addItem = useCallback((item: Omit<CartItem, 'quantity'>, quantity = 1) => {
        setItems(current => {
            const existing = current.find(entry => entry.id === item.id)

            if (existing) return current.map(entry => entry.id === item.id ? { ...entry, ...item, quantity: Math.min(entry.quantity + quantity, item.stock) } : entry)

            if (current.length >= MAX_CART_LINES) return current

            return [...current, { ...item, quantity: Math.min(quantity, item.stock) }]
        })
    }, [])

    const setQuantity = useCallback((id: string, quantity: number) => {
        setItems(current => current
            .map(entry => entry.id === id ? { ...entry, quantity: Math.max(0, Math.min(quantity, entry.stock)) } : entry)
            .filter(entry => entry.quantity > 0))
    }, [])

    const removeItem = useCallback((id: string) => setItems(current => current.filter(entry => entry.id !== id)), [])

    const clearCart = useCallback(() => setItems([]), [])

    const value = useMemo<ShopContextValue>(() => ({
        items,
        count: items.reduce((sum, item) => sum + item.quantity, 0),
        addItem,
        setQuantity,
        removeItem,
        clearCart,
        cartOpen,
        setCartOpen,
        currency,
        setCurrency,
        rates
    }), [items, addItem, setQuantity, removeItem, clearCart, cartOpen, currency, setCurrency, rates])

    return (
        <ShopContext.Provider value={value}>
            {children}
        </ShopContext.Provider>
    )
}

export const useShop = (): ShopContextValue => {
    const context = useContext(ShopContext)

    if (!context) throw new Error('useShop must be used inside ShopProvider.')

    return context
}
